import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { IconPickerItem } from 'react-fa-icon-picker';
import InputMask from 'react-input-mask';
import { FormattedMessage } from 'react-intl';
import injectSheet from 'react-jss';
import FormLabel from '../FormLabel';
import Hint from '../Hint';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import LoadingIcon from '../icons/LoadingIcon';

class TextInput extends Component {
  getComponentAndProps() {
    const { variant, mask } = this.props;

    switch (variant) {
      case 'multiline':
        return { Component: 'textarea', props: { rows: 1 } };

      case 'select':
        return { Component: 'select', props: {} };

      default:
        return { Component: mask ? InputMask : 'input', props: { type: 'text' } };
    }
  }

  render() {
    const {
      classes,
      className,
      style,
      sheet,
      meta,
      theme,
      input: { value, onChange },
      placeholder,
      label,
      required,
      hint,
      hintPosition,
      variant,
      loading,
      disabled,
      icon,
      ...other
    } = this.props;
    const error = Boolean(meta && meta.touched && meta.error);
    const { Component, props } = this.getComponentAndProps();

    const iconStyles = {
      position: 'absolute',
      bottom: 1,
      left: 12,
    };

    return (
      <div
        className={classNames(
          classes.root,
          label && classes.withLabel,
          error && classes.withError,
          disabled && classes.disabled,
          className,
        )}
        style={style}
      >
        {icon && <IconPickerItem containerStyles={iconStyles} icon={icon} color="#505050" />}
        <Component
          placeholder={placeholder}
          className={classNames(
            classes.input,
            variant === 'select' && classes.select,
            Boolean(icon) && classes.withIcon,
          )}
          value={value}
          onChange={onChange}
          rows={1}
          disabled={disabled}
          {...props}
          {...other}
        />
        {label && (
          <FormLabel component="label" className={classNames(classes.caption, classes.label)}>
            {label}
            {hint && <Hint messageId={hint} position={hintPosition} />}
          </FormLabel>
        )}
        {(error || required) && (
          <FormLabel component="span" className={classNames(classes.caption, classes.required)}>
            {meta.error || <FormattedMessage id="required" />}
          </FormLabel>
        )}
        {loading ? (
          <LoadingIcon
            className={classNames(classes.icon, (error || required) && classes.iconDown)}
          />
        ) : (
          variant === 'select' && (
            <ArrowDownIcon
              className={classNames(classes.icon, (error || required) && classes.iconDown)}
            />
          )
        )}
      </div>
    );
  }
}

TextInput.propTypes = {
  classes: PropTypes.object.isRequired,
  input: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['default', 'multiline', 'select']),
  theme: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object,
  placeholder: PropTypes.string,
  label: PropTypes.node,
  sheet: PropTypes.object,
  meta: PropTypes.object,
  hint: PropTypes.string,
  hintPosition: PropTypes.oneOf(['top', 'right']),
  mask: PropTypes.string,
  required: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

TextInput.defaultProps = {
  variant: 'default',
  theme: undefined,
  className: undefined,
  style: undefined,
  placeholder: undefined,
  label: undefined,
  sheet: undefined,
  meta: undefined,
  hint: undefined,
  hintPosition: undefined,
  mask: undefined,
  required: false,
  loading: false,
  disabled: false,
};

const stylesheet = theme => ({
  root: {
    position: 'relative',
  },
  withLabel: {
    // empty class to be used by its children
  },
  withError: {
    // empty class to be used by its children
  },
  disabled: {
    opacity: 0.5,
  },
  withIcon: {
    // empty class
  },
  input: {
    display: 'block',
    width: '100%',
    height: '100%',
    fontSize: '1em',
    fontFamily: 'inherit',
    color: theme.palette.common.darkBlack,
    border: '1px solid rgba(0, 0, 0, 0.25)',
    borderRadius: 2,
    padding: [[11, 15]], // because of the 1px border
    transition: theme.transitions.create(['border-color', 'background-color']),
    resize: 'vertical',
    appearance: 'none',
    backgroundColor: theme.palette.common.white,
    '&::placeholder': {
      color: theme.palette.common.minBlack,
    },
    '$withLabel &': {
      padding: [[27, 15, 9]], // because of the 1px border
    },
    '$withError &': {
      border: [[2, 'solid', theme.palette.error[500]]],
      padding: [[10, 14]], // because of the 2px border
    },
    '$withLabel$withError &': {
      padding: [[26, 14, 8]], // because of the 2px border
    },
    '$disabled &': {
      backgroundColor: theme.palette.common.faintBlack,
    },
    '&$withIcon': {
      paddingLeft: 41,
    },
  },
  select: {
    paddingRight: 55, // because of the 1px border
    '$withLabel &': {
      paddingRight: 55, // because of the 1px border
    },
    '$withError &, $withLabel$withError &': {
      paddingRight: 54, // because of the 2px border
    },
  },
  caption: {
    position: 'absolute',
    top: 8,
  },
  label: {
    left: 16,
  },
  required: {
    right: 16,
    '$withError &': {
      color: theme.palette.error[500],
    },
  },
  icon: {
    position: 'absolute',
    right: theme.spacing.small,
    bottom: theme.spacing.small,
    pointerEvents: 'none',
  },
  iconDown: {
    bottom: theme.spacing.unit,
  },
});

export default injectSheet(stylesheet)(TextInput);



// WEBPACK FOOTER //
// ./src/site-render/src/TextInput/TextInput.js